import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from "recharts";
import { CurrencyDollarIcon, WrenchIcon, TruckIcon, ReceiptIcon, StorefrontIcon, PackageIcon, GasPumpIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { sortByUnit } from "@/lib/unitSort";
import FuelGauge from "@/components/FuelGauge";

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [shopSpend, setShopSpend] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [mpg, setMpg] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [selectedAssetKey, setSelectedAssetKey] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Independent date filter for fuel — defaults to last 30 days
  const [fuelFrom, setFuelFrom] = useState("");
  const [fuelTo, setFuelTo] = useState("");
  const [fuelDays, setFuelDays] = useState(30);

  const loadFuel = useCallback(async () => {
    const params = new URLSearchParams();
    if (fuelFrom || fuelTo) {
      if (fuelFrom) params.set("from", fuelFrom);
      if (fuelTo) params.set("to", fuelTo);
    } else {
      params.set("days", String(fuelDays));
    }
    try {
      const [mp, pu] = await Promise.all([
        api.get(`/reports/mpg?${params.toString()}`),
        api.get(`/reports/fuel-purchases?${params.toString()}`),
      ]);
      setMpg(mp.data);
      setPurchases(pu.data);
    } catch { /* ignore */ }
  }, [fuelFrom, fuelTo, fuelDays]);

  useEffect(() => {
    (async () => {
      const [s, t, tr, r, ss, al] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/trucks"),
        api.get("/trailers"),
        api.get("/repairs"),
        api.get("/reports/shop-spend"),
        api.get("/parts/allocations"),
      ]);
      setStats(s.data);
      setTrucks(t.data);
      setTrailers(tr.data);
      setRepairs(r.data);
      setShopSpend(ss.data);
      setAllocations(al.data || []);
    })();
  }, []);

  useEffect(() => { loadFuel(); }, [loadFuel]);

  const exportFuelCsv = () => {
    if (!purchases?.rows?.length) return;
    const header = ["Date", "Unit", "Driver", "Before %", "After %", "Actual gal", "Est. gal", "Cost $", "Odometer", "Miles since last", "MPG", "Receipt", "Notes"];
    const csv = [header.join(",")].concat(
      purchases.rows.map((r) => [
        new Date(r.recorded_at).toISOString(),
        r.unit_number || "",
        (r.driver_name || "").replace(/,/g, " "),
        r.before_pct ?? "",
        r.after_pct ?? "",
        r.actual_gallons ?? "",
        r.estimated_gallons ?? "",
        r.cost ?? "",
        r.odometer ?? "",
        r.miles_since_last ?? "",
        r.mpg_since_last ?? "",
        r.receipt_status || "",
        (r.notes || "").replace(/[",\n]/g, " "),
      ].join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fuel-purchases-${fuelFrom || fuelDays + "d"}-to-${fuelTo || "now"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const assetOptions = useMemo(() => [
    ...sortByUnit(trucks).map((t) => ({ key: `truck:${t.id}`, label: `Truck #${t.unit_number}`, group: "trucks" })),
    ...sortByUnit(trailers).map((t) => ({ key: `trailer:${t.id}`, label: `Trailer #${t.unit_number}`, group: "trailers" })),
  ], [trucks, trailers]);

  const selectedAsset = useMemo(() => {
    if (!selectedAssetKey) return null;
    const [kind, id] = selectedAssetKey.split(":");
    const list = kind === "truck" ? trucks : trailers;
    const asset = list.find((x) => x.id === id);
    return asset ? { ...asset, _kind: kind } : null;
  }, [selectedAssetKey, trucks, trailers]);

  const assetRepairs = useMemo(() => {
    if (!selectedAsset) return [];
    const key = selectedAsset._kind === "truck" ? "truck_id" : "trailer_id";
    return repairs
      .filter((r) => r[key] === selectedAsset.id)
      .filter((r) => (!fromDate || (r.repair_date || "") >= fromDate) && (!toDate || (r.repair_date || "") <= toDate))
      .sort((a, b) => (b.repair_date || "").localeCompare(a.repair_date || ""));
  }, [selectedAsset, repairs, fromDate, toDate]);

  const assetSpend = assetRepairs.reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const byCategory = useMemo(() => {
    const m = {};
    assetRepairs.forEach((r) => {
      const k = r.category || "other";
      m[k] = (m[k] || 0) + Number(r.total_cost || 0);
    });
    return Object.entries(m).map(([category, cost]) => ({ category, cost: Math.round(cost * 100) / 100 })).sort((a, b) => b.cost - a.cost);
  }, [assetRepairs]);

  if (!stats) return <div className="p-12 font-mono text-sm text-slate-500">LOADING...</div>;

  // Cost per truck AND per trailer
  const cost_per_truck = trucks.map((t) => {
    const c = repairs.filter((r) => r.truck_id === t.id).reduce((sum, r) => sum + Number(r.total_cost || 0), 0);
    return { name: `#${t.unit_number}`, cost: Math.round(c * 100) / 100 };
  }).sort((a, b) => b.cost - a.cost).slice(0, 10);

  const cost_per_trailer = trailers.map((t) => {
    const c = repairs.filter((r) => r.trailer_id === t.id).reduce((sum, r) => sum + Number(r.total_cost || 0), 0);
    return { name: `#${t.unit_number}`, cost: Math.round(c * 100) / 100 };
  }).sort((a, b) => b.cost - a.cost).slice(0, 10);

  const trailer_total_spend = repairs.filter((r) => r.trailer_id).reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const truck_total_spend = repairs.filter((r) => r.truck_id).reduce((s, r) => s + Number(r.total_cost || 0), 0);

  const avg_per_truck = trucks.length > 0 ? truck_total_spend / trucks.length : 0;
  const avg_per_repair = repairs.length > 0 ? stats.total_repair_cost / repairs.length : 0;

  // In-house vs Outside split
  const in_house_spend = repairs.filter((r) => r.repair_type !== "outside").reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const outside_spend = repairs.filter((r) => r.repair_type === "outside").reduce((s, r) => s + Number(r.total_cost || 0), 0);
  const total_outside_repairs = repairs.filter((r) => r.repair_type === "outside").length;

  // Parts purchased per unit (from allocations) — aggregates ALL history, not just open
  const partsPerUnit = (() => {
    const m = {};
    (allocations || []).forEach((a) => {
      const key = `${a.unit_type}:${a.unit_id}`;
      if (!m[key]) m[key] = {
        key, unit_type: a.unit_type, unit_id: a.unit_id, unit_number: a.unit_number,
        line_count: 0, qty_total: 0, spent: 0, open_qty: 0, open_value: 0,
      };
      const qty = Number(a.qty_purchased) || 0;
      const rem = Number(a.qty_remaining) || 0;
      const uc = Number(a.unit_cost) || 0;
      m[key].line_count += 1;
      m[key].qty_total += qty;
      m[key].spent += qty * uc;
      m[key].open_qty += rem;
      m[key].open_value += rem * uc;
    });
    return Object.values(m).sort((a, b) => b.spent - a.spent);
  })();

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Analytics" title="Cost & expense reports" subtitle="Track maintenance spend across your fleet." />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Spend" value={`$${Number(stats.total_repair_cost).toLocaleString()}`} icon={CurrencyDollarIcon} testid="report-total-spend" />
          <StatCard label="Total Repairs" value={stats.total_repairs} icon={WrenchIcon} testid="report-total-repairs" />
          <StatCard label="Avg / Truck" value={`$${Math.round(avg_per_truck).toLocaleString()}`} icon={TruckIcon} testid="report-avg-truck" />
          <StatCard label="Avg / Repair" value={`$${Math.round(avg_per_repair).toLocaleString()}`} icon={ReceiptIcon} testid="report-avg-repair" />
        </div>

        {/* In-house vs Outside split */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="in-house-spend-card">
            <div className="section-eyebrow mb-1">/// In-house</div>
            <div className="font-display text-4xl font-black tracking-tighter tabular-nums">${Number(in_house_spend).toLocaleString()}</div>
            <div className="text-sm text-slate-600 mt-1">Parts cost from {repairs.length - total_outside_repairs} in-house repairs</div>
            <div className="mt-3 h-2 bg-slate-100 rounded-sm overflow-hidden">
              <div className="h-full bg-blue-600" style={{ width: `${(in_house_spend + outside_spend > 0) ? (in_house_spend / (in_house_spend + outside_spend) * 100) : 0}%` }} />
            </div>
          </div>
          <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="outside-spend-card">
            <div className="section-eyebrow mb-1" style={{ color: "#D97706" }}>/// Outside shops</div>
            <div className="font-display text-4xl font-black tracking-tighter tabular-nums">${Number(outside_spend).toLocaleString()}</div>
            <div className="text-sm text-slate-600 mt-1">Shop bills from {total_outside_repairs} outside repairs</div>
            <div className="mt-3 h-2 bg-slate-100 rounded-sm overflow-hidden">
              <div className="h-full bg-amber-600" style={{ width: `${(in_house_spend + outside_spend > 0) ? (outside_spend / (in_house_spend + outside_spend) * 100) : 0}%` }} />
            </div>
          </div>
        </div>

        {/* Fuel economy + purchases (Motive + driver-uploaded receipts) */}
        {mpg && (
          <div className="bg-white border border-slate-300 rounded-sm" data-testid="mpg-section">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="section-eyebrow mb-0.5">/// Fuel · Motive + driver receipts</div>
                <h3 className="font-display text-base font-bold tracking-tight flex items-center gap-2">
                  <GasPumpIcon size={16} weight="bold" /> Fleet fuel report
                </h3>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-mono text-slate-500">Range:</span>
                <Input type="date" value={fuelFrom} onChange={(e) => setFuelFrom(e.target.value)} className="h-8 rounded-sm text-xs w-36" data-testid="fuel-from" />
                <span className="text-slate-400">→</span>
                <Input type="date" value={fuelTo} onChange={(e) => setFuelTo(e.target.value)} className="h-8 rounded-sm text-xs w-36" data-testid="fuel-to" />
                {(fuelFrom || fuelTo) ? (
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => { setFuelFrom(""); setFuelTo(""); }}>Clear</Button>
                ) : (
                  <Select value={String(fuelDays)} onValueChange={(v) => setFuelDays(Number(v))}>
                    <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="fuel-days">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last year</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={exportFuelCsv} data-testid="fuel-csv" disabled={!purchases?.rows?.length}>
                  <DownloadSimpleIcon size={12} weight="bold" className="mr-1" /> CSV
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-5 border-b border-slate-100">
              <div>
                <div className="font-mono text-[10px] uppercase text-slate-500">Total spend</div>
                <div className="font-display text-2xl font-black tabular-nums text-emerald-700">${Number(mpg.company?.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase text-slate-500">Gallons burned</div>
                <div className="font-display text-2xl font-black tabular-nums">{Number(mpg.company?.total_gallons || 0).toLocaleString()}</div>
                <div className="text-[10px] text-slate-500 font-mono">{Number(mpg.company?.actual_gallons_from_receipts || 0).toLocaleString()} from receipts</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase text-slate-500">Miles</div>
                <div className="font-display text-2xl font-black tabular-nums">{Number(mpg.company?.total_miles || 0).toLocaleString()}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase text-slate-500">Fleet MPG</div>
                <div className="font-display text-2xl font-black tabular-nums">{mpg.company?.mpg ?? "—"}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase text-slate-500">Cost / mile</div>
                <div className="font-display text-2xl font-black tabular-nums">{mpg.company?.cost_per_mile != null ? `$${mpg.company.cost_per_mile.toFixed(3)}` : "—"}</div>
                <div className="text-[10px] text-slate-500 font-mono">{mpg.company?.cost_per_gallon != null ? `$${mpg.company.cost_per_gallon.toFixed(3)}/gal` : ""}</div>
              </div>
            </div>

            {mpg.trucks.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                No fill-ups detected in this range yet. Set each truck&apos;s tank size and make sure drivers upload receipts to power gallons + cost.
              </div>
            ) : (
              <table className="fleet-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Vehicle</th>
                    <th>Current fuel</th>
                    <th>Fill-ups</th>
                    <th>Actual gal</th>
                    <th>Est. gal</th>
                    <th>Cost</th>
                    <th>Miles</th>
                    <th>MPG</th>
                    <th>$/mi</th>
                  </tr>
                </thead>
                <tbody>
                  {sortByUnit(mpg.trucks).map((row) => (
                    <tr key={row.truck_id} data-testid={`mpg-row-${row.truck_id}`}>
                      <td className="font-mono font-bold">#{row.unit_number}</td>
                      <td className="text-xs text-slate-600">{row.label}</td>
                      <td>{row.current_fuel_percentage != null ? <FuelGauge percentage={row.current_fuel_percentage} size="sm" /> : "—"}</td>
                      <td className="font-mono tabular-nums">
                        {row.fill_ups}
                        {row.fill_ups_with_receipt > 0 && (
                          <span className="text-[10px] text-emerald-700 ml-1">({row.fill_ups_with_receipt}✓)</span>
                        )}
                      </td>
                      <td className="font-mono tabular-nums">{row.actual_gallons > 0 ? row.actual_gallons.toLocaleString() : "—"}</td>
                      <td className="font-mono tabular-nums text-slate-500">{row.estimated_gallons > 0 ? row.estimated_gallons.toLocaleString() : "—"}</td>
                      <td className="font-mono tabular-nums font-bold text-emerald-700">{row.total_cost > 0 ? `$${row.total_cost.toLocaleString()}` : "—"}</td>
                      <td className="font-mono tabular-nums">{Number(row.total_miles).toLocaleString()}</td>
                      <td className="font-mono tabular-nums font-bold">{row.mpg ?? "—"}</td>
                      <td className="font-mono tabular-nums">{row.cost_per_mile != null ? `$${row.cost_per_mile.toFixed(3)}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Detailed fuel-purchase log */}
            {purchases?.rows?.length > 0 && (
              <div className="border-t border-slate-200" data-testid="fuel-purchases-section">
                <div className="px-5 py-3 border-b border-slate-100">
                  <div className="section-eyebrow mb-0.5">/// Fuel purchases</div>
                  <div className="font-mono text-xs text-slate-500">
                    {purchases.totals.count} fill-ups · {purchases.totals.with_receipt} with receipt · ${Number(purchases.totals.cost).toLocaleString(undefined, { minimumFractionDigits: 2 })} · {Number(purchases.totals.actual_gallons).toLocaleString()} gal actual
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="fleet-table text-xs">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Unit</th>
                        <th>Driver</th>
                        <th>Before → After</th>
                        <th>Actual gal</th>
                        <th>Est. gal</th>
                        <th>Cost</th>
                        <th>Odometer</th>
                        <th>Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.rows.map((r) => (
                        <tr key={r.id} data-testid={`purchase-${r.id}`}>
                          <td className="font-mono">{new Date(r.recorded_at).toLocaleString()}</td>
                          <td className="font-mono font-bold">#{r.unit_number}</td>
                          <td>{r.driver_name}</td>
                          <td className="font-mono">{Math.round(r.before_pct || 0)}% → {Math.round(r.after_pct || 0)}%</td>
                          <td className="font-mono tabular-nums font-bold">{r.actual_gallons != null ? `${r.actual_gallons} gal` : "—"}</td>
                          <td className="font-mono tabular-nums text-slate-500">{r.estimated_gallons != null ? r.estimated_gallons : "—"}</td>
                          <td className="font-mono tabular-nums font-bold text-emerald-700">{r.cost != null ? `$${Number(r.cost).toFixed(2)}` : "—"}</td>
                          <td className="font-mono tabular-nums">{r.odometer != null ? Number(r.odometer).toLocaleString() : "—"}</td>
                          <td>
                            {r.receipt_status === "uploaded" ? (
                              <span className="text-emerald-700 font-bold" title={r.notes}>✓ {r.receipt_attachments.length} file{r.receipt_attachments.length !== 1 ? "s" : ""}</span>
                            ) : r.receipt_status === "dismissed" ? (
                              <span className="text-slate-400">Dismissed</span>
                            ) : r.receipt_status === "no_driver" ? (
                              <span className="text-slate-400">No driver</span>
                            ) : (
                              <span className="text-amber-700 font-bold">Pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-shop spend table + chart */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="shop-spend-section">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="section-eyebrow mb-0.5">/// Vendor spend</div>
              <h3 className="font-display text-base font-bold tracking-tight flex items-center gap-2">
                <StorefrontIcon size={16} weight="bold" /> Spend by repair shop
              </h3>
            </div>
            <span className="font-mono text-xs text-slate-500">{shopSpend.length} shops</span>
          </div>

          {shopSpend.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No outside repairs logged yet.</div>
          ) : (
            <>
              <div className="p-4 h-64 border-b border-slate-200">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={shopSpend.slice(0, 10)} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                    <YAxis type="category" dataKey="shop_name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} width={140} />
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="total_spend" fill="#D97706" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <table className="fleet-table">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>Specialties</th>
                    <th>Repairs</th>
                    <th>Total spend</th>
                    <th>Avg / repair</th>
                    <th>Last visit</th>
                  </tr>
                </thead>
                <tbody>
                  {shopSpend.map((s) => (
                    <tr key={s.shop_id || s.shop_name} data-testid={`shop-spend-row-${s.shop_id || "unknown"}`}>
                      <td className="font-semibold">{s.shop_name}</td>
                      <td className="text-xs text-slate-600">{s.specialties || "—"}</td>
                      <td className="font-mono tabular-nums">{s.repair_count}</td>
                      <td className="font-mono tabular-nums font-bold">${Number(s.total_spend).toLocaleString()}</td>
                      <td className="font-mono tabular-nums">${Number(s.average_per_repair).toLocaleString()}</td>
                      <td className="font-mono">{s.last_repair_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="section-eyebrow mb-0.5">/// Trend</div>
            <h3 className="font-display text-base font-bold tracking-tight">Monthly maintenance spend</h3>
          </div>
          <div className="p-4 h-80">
            {stats.cost_by_month.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.cost_by_month}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                  <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Line type="monotone" dataKey="cost" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* TRUCK vs TRAILER split */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="truck-total-spend-card">
            <div className="section-eyebrow mb-1 flex items-center gap-1"><TruckIcon size={12} weight="bold" /> Truck spend</div>
            <div className="font-display text-4xl font-black tracking-tighter tabular-nums">${Number(truck_total_spend).toLocaleString()}</div>
            <div className="text-sm text-slate-600 mt-1">Across {trucks.length} trucks · {repairs.filter((r) => r.truck_id).length} repairs</div>
          </div>
          <div className="bg-white border border-slate-300 rounded-sm p-6" data-testid="trailer-total-spend-card">
            <div className="section-eyebrow mb-1 flex items-center gap-1"><PackageIcon size={12} weight="bold" /> Trailer spend</div>
            <div className="font-display text-4xl font-black tracking-tighter tabular-nums">${Number(trailer_total_spend).toLocaleString()}</div>
            <div className="text-sm text-slate-600 mt-1">Across {trailers.length} trailers · {repairs.filter((r) => r.trailer_id).length} repairs</div>
          </div>
        </div>

        {/* SPEND BY TRUCK + SPEND BY TRAILER */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-300 rounded-sm">
            <div className="px-5 py-3 border-b border-slate-200">
              <div className="section-eyebrow mb-0.5">/// By truck</div>
              <h3 className="font-display text-base font-bold tracking-tight">Top 10 trucks by spend</h3>
            </div>
            <div className="p-4 h-72">
              {cost_per_truck.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cost_per_truck} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} width={80} />
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="cost" fill="#2563EB" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="bg-white border border-slate-300 rounded-sm">
            <div className="px-5 py-3 border-b border-slate-200">
              <div className="section-eyebrow mb-0.5">/// By trailer</div>
              <h3 className="font-display text-base font-bold tracking-tight">Top 10 trailers by spend</h3>
            </div>
            <div className="p-4 h-72">
              {cost_per_trailer.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">No trailer repairs logged</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cost_per_trailer} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} width={80} />
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="cost" fill="#0F766E" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Parts purchased per unit */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="parts-per-unit-section">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="section-eyebrow mb-0.5">/// Parts spend</div>
              <h3 className="font-display text-base font-bold tracking-tight flex items-center gap-2">
                <PackageIcon size={16} weight="bold" /> Parts purchased per unit
              </h3>
              <div className="text-xs text-slate-500 mt-0.5">Aggregated from parts-invoice unit assignments. Includes both consumed and open reservations.</div>
            </div>
            <span className="font-mono text-xs text-slate-500">{partsPerUnit.length} units</span>
          </div>

          {partsPerUnit.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No unit-assigned parts yet. Assign a part to a unit during a parts invoice upload to see it here.
            </div>
          ) : (
            <>
              <div className="p-4 h-64 border-b border-slate-200">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={partsPerUnit.slice(0, 10).map((u) => ({ name: (u.unit_type === "truck" ? "🚚 " : "🚛 ") + (u.unit_number || u.unit_id.slice(0, 6)), spent: Math.round(u.spent) }))} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} width={110} />
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="spent" fill="#059669" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <table className="fleet-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th className="text-right">Lines on invoices</th>
                    <th className="text-right">Total qty bought</th>
                    <th className="text-right">Total spent</th>
                    <th className="text-right">Still reserved (qty)</th>
                    <th className="text-right">Still reserved ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {partsPerUnit.map((u) => (
                    <tr key={u.key} data-testid={`parts-per-unit-row-${u.unit_type}-${u.unit_id}`}>
                      <td className="font-semibold whitespace-nowrap">
                        {u.unit_type === "truck" ? "🚚 Unit" : "🚛 Trailer"} {u.unit_number || u.unit_id.slice(0, 8)}
                      </td>
                      <td className="font-mono tabular-nums text-right">{u.line_count}</td>
                      <td className="font-mono tabular-nums text-right">{u.qty_total}</td>
                      <td className="font-mono tabular-nums text-right font-bold">${Math.round(u.spent).toLocaleString()}</td>
                      <td className="font-mono tabular-nums text-right text-emerald-700">{u.open_qty}</td>
                      <td className="font-mono tabular-nums text-right text-emerald-700">${Math.round(u.open_value).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        {/* PER-ASSET DRILL DOWN */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="asset-report-card">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="section-eyebrow mb-0.5">/// Drill down</div>
            <h3 className="font-display text-base font-bold tracking-tight">Full repair history for a truck or trailer</h3>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase font-mono text-slate-500 font-bold">Asset</label>
              <Select value={selectedAssetKey} onValueChange={setSelectedAssetKey}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="asset-report-select"><SelectValue placeholder="Pick a truck or trailer" /></SelectTrigger>
                <SelectContent>
                  {assetOptions.length === 0 && <div className="p-3 text-xs text-slate-500">No assets yet</div>}
                  {assetOptions.map((o) => (<SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-mono text-slate-500 font-bold">From</label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm mt-1" data-testid="asset-report-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-mono text-slate-500 font-bold">To</label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm mt-1" data-testid="asset-report-to" />
            </div>
          </div>

          {selectedAsset ? (
            <>
              <div className="px-5 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="text-[10px] font-mono uppercase text-slate-500">Total spend</div>
                  <div className="font-display text-2xl font-black tabular-nums">${Number(assetSpend).toLocaleString()}</div>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="text-[10px] font-mono uppercase text-slate-500">Repairs</div>
                  <div className="font-display text-2xl font-black tabular-nums">{assetRepairs.length}</div>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="text-[10px] font-mono uppercase text-slate-500">Avg / repair</div>
                  <div className="font-display text-2xl font-black tabular-nums">${assetRepairs.length ? Math.round(assetSpend / assetRepairs.length).toLocaleString() : "0"}</div>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="text-[10px] font-mono uppercase text-slate-500">Categories</div>
                  <div className="font-display text-2xl font-black tabular-nums">{byCategory.length}</div>
                </div>
              </div>
              {byCategory.length > 0 && (
                <div className="px-5 pb-4">
                  <div className="text-[10px] font-mono uppercase text-slate-500 mb-2">Spend by category</div>
                  <div className="flex flex-wrap gap-2">
                    {byCategory.map((c) => (
                      <div key={c.category} className="px-3 py-1 bg-blue-50 border border-blue-200 rounded-sm text-xs">
                        <span className="font-mono uppercase text-blue-800">{c.category.replace(/_/g, " ")}</span>
                        <span className="ml-2 font-bold text-blue-900">${c.cost.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <table className="fleet-table" data-testid="asset-repairs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Mileage</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {assetRepairs.map((r) => (
                    <tr key={r.id} data-testid={`asset-repair-${r.id}`}>
                      <td className="font-mono">{r.repair_date || "—"}</td>
                      <td className="font-semibold">{r.title}</td>
                      <td className="text-xs uppercase font-mono">{r.category?.replace(/_/g, " ") || "—"}</td>
                      <td className="text-xs uppercase font-mono">{r.repair_type?.replace("_", "-") || "—"}</td>
                      <td className="font-mono tabular-nums">{r.mileage ? Number(r.mileage).toLocaleString() : "—"}</td>
                      <td className="font-mono tabular-nums font-bold text-blue-700">${Number(r.total_cost || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                  {assetRepairs.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500">No repairs for this asset in the selected range.</td></tr>
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <div className="p-8 text-center text-sm text-slate-500">Select a truck or trailer above to see its full repair history and total spend.</div>
          )}
        </div>

        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="section-eyebrow mb-0.5">/// Top spenders</div>
            <h3 className="font-display text-base font-bold tracking-tight">Repair cost by truck (top 10)</h3>
          </div>
          <div className="p-4 h-96">
            {cost_per_truck.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cost_per_truck} layout="vertical">
                  <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                  <YAxis type="category" dataKey="truck" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} width={80} />
                  <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12 }} formatter={(v) => `$${Number(v).toLocaleString()}`} />
                  <Bar dataKey="cost" fill="#0F172A" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
